import React from 'react';
import { Button, Card, Col, Input, Modal, Row, Space, Typography } from 'antd';
import StyleSlider from '../widgets/StyleSlider';
import InferenceConfidenceBadge from './InferenceConfidenceBadge';
import StyleCandidateSelector from './StyleCandidateSelector';

const { Text, Title } = Typography;
const { TextArea } = Input;

function FieldHeader({ label, confidence }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
      <Text strong>{label}</Text>
      {confidence ? <InferenceConfidenceBadge value={confidence} compact /> : null}
    </div>
  );
}

function ProgressMini({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 108 }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: current >= 1 ? '#22c55e' : '#e5e7eb', color: current >= 1 ? '#fff' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10 }}>1</div>
      <div style={{ height: 3, flex: 1, background: '#dbe3ef', borderRadius: 999, position: 'relative', overflow: 'hidden' }}>
        <div style={{ width: current === 2 ? '100%' : '45%', height: '100%', background: '#2563eb' }} />
      </div>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: current >= 2 ? '#2563eb' : '#e5e7eb', color: current >= 2 ? '#fff' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10 }}>2</div>
    </div>
  );
}

export default function AgentConfigReviewModal({
  visible,
  configDraft,
  styleCandidates,
  selectedStyleCandidateId,
  fieldConfidenceMap,
  onChange,
  onApply,
  onBack,
  onSaveDraft,
}) {
  const style = configDraft.style || {
    response_length: 50,
    emoji_level: 50,
    formality_level: 50,
  };
  const strategy = configDraft.strategy || {
    tone: '',
    key_phrases: [],
    objection_handling_guide: '',
    name: '',
  };
  const guardrails = configDraft.guardrails || {
    fallback_message: '',
    max_response_length: 500,
  };

  const patchConfig = (patch) => onChange({ configDraft: { ...configDraft, ...patch } });
  const patchStyle = (field, value) => patchConfig({ style: { ...style, [field]: value } });
  const patchStrategy = (patch) => patchConfig({ strategy: { ...strategy, ...patch } });
  const patchGuardrails = (patch) => patchConfig({ guardrails: { ...guardrails, ...patch } });

  return (
    <Modal
      open={visible}
      onCancel={onBack}
      footer={null}
      width={1340}
      title={null}
      centered
      destroyOnHidden
      styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: 'hidden' } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '82vh' }}>
        <div style={{ padding: '10px 18px', borderBottom: '1px solid #eef2f7', background: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Bước 2 / 2</Text>
                <Title level={4} style={{ margin: 0, lineHeight: 1.15 }}>Duyệt cấu hình Agent</Title>
              </div>
              <Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 13 }}>
                Chọn phong cách nền và chỉnh lại cấu hình trước khi lưu áp dụng.
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e5e7eb', flexShrink: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, margin: 0 }}>Tiến trình</Text>
              <ProgressMini current={2} />
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 20px', background: '#fafcff' }}>
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: 16, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Phong cách trả lời được phát hiện</Text>
                  <Title level={5} style={{ margin: '4px 0 0' }}>Chọn một phong cách nền</Title>
                </div>
                <StyleCandidateSelector
                  candidates={styleCandidates}
                  value={selectedStyleCandidateId}
                  onChange={(nextValue) => onChange({ selectedStyleCandidateId: nextValue })}
                />
              </Space>
            </Card>

            <Row gutter={20} align="top">
              <Col xs={24} xl={17}>
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                  <Card bodyStyle={{ padding: 18 }} style={{ borderRadius: 18, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Section 1</Text>
                        <Title level={5} style={{ margin: '4px 0 0' }}>Định danh & chào hỏi</Title>
                      </div>

                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <FieldHeader label="Tên Agent" confidence={fieldConfidenceMap?.agentName} />
                          <Input value={configDraft.agentName || ''} onChange={(event) => patchConfig({ agentName: event.target.value })} />
                        </Col>
                        <Col xs={24} md={12}>
                          <FieldHeader label="Vai trò" confidence={fieldConfidenceMap?.agentRole} />
                          <Input value={configDraft.agentRole || ''} onChange={(event) => patchConfig({ agentRole: event.target.value })} />
                        </Col>
                      </Row>

                      <div>
                        <FieldHeader label="Greeting message" confidence={fieldConfidenceMap?.greetingMessage} />
                        <Input value={configDraft.greetingMessage || ''} onChange={(event) => patchConfig({ greetingMessage: event.target.value })} />
                      </div>

                      <div>
                        <FieldHeader label="Personality" confidence={fieldConfidenceMap?.agentPersonality} />
                        <TextArea rows={3} value={configDraft.agentPersonality || ''} onChange={(event) => patchConfig({ agentPersonality: event.target.value })} />
                      </div>
                    </Space>
                  </Card>

                  <Card bodyStyle={{ padding: 18 }} style={{ borderRadius: 18, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Section 2</Text>
                        <Title level={5} style={{ margin: '4px 0 0' }}>Prompting & phong cách phản hồi</Title>
                      </div>

                      <div>
                        <FieldHeader label="System prompt" confidence={fieldConfidenceMap?.systemPrompt} />
                        <TextArea rows={6} value={configDraft.systemPrompt || ''} onChange={(event) => patchConfig({ systemPrompt: event.target.value })} />
                      </div>

                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <StyleSlider label="Độ dài phản hồi" leftLabel="Ngắn" rightLabel="Dài" value={style.response_length} onChange={(value) => patchStyle('response_length', value)} />
                        </Col>
                        <Col xs={24} md={8}>
                          <StyleSlider label="Emoji" leftLabel="Ít" rightLabel="Nhiều" value={style.emoji_level} onChange={(value) => patchStyle('emoji_level', value)} color="#52c41a" />
                        </Col>
                        <Col xs={24} md={8}>
                          <StyleSlider label="Trang trọng" leftLabel="Thân mật" rightLabel="Trang trọng" value={style.formality_level} onChange={(value) => patchStyle('formality_level', value)} color="#fa8c16" />
                        </Col>
                      </Row>
                    </Space>
                  </Card>

                  <Card bodyStyle={{ padding: 18 }} style={{ borderRadius: 18, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary">Section 3</Text>
                        <Title level={5} style={{ margin: '4px 0 0' }}>Chiến lược & guardrails</Title>
                      </div>

                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <FieldHeader label="Tên chiến lược" confidence={fieldConfidenceMap?.['strategy.name']} />
                          <Input value={strategy.name || ''} onChange={(event) => patchStrategy({ name: event.target.value })} />
                        </Col>
                        <Col xs={24} md={12}>
                          <FieldHeader label="Giọng điệu" confidence={fieldConfidenceMap?.['strategy.tone']} />
                          <Input value={strategy.tone || ''} onChange={(event) => patchStrategy({ tone: event.target.value })} />
                        </Col>
                      </Row>

                      <div>
                        <FieldHeader label="Hướng dẫn xử lý objection" confidence={fieldConfidenceMap?.['strategy.objection_handling_guide']} />
                        <TextArea rows={3} value={strategy.objection_handling_guide || ''} onChange={(event) => patchStrategy({ objection_handling_guide: event.target.value })} />
                      </div>

                      <div>
                        <FieldHeader label="Key phrases" confidence={fieldConfidenceMap?.['strategy.key_phrases']} />
                        <Input
                          value={(strategy.key_phrases || []).join(', ')}
                          onChange={(event) => patchStrategy({
                            key_phrases: event.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                          })}
                        />
                      </div>

                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <FieldHeader label="Fallback message" confidence={fieldConfidenceMap?.['guardrails.fallback_message']} />
                          <TextArea rows={3} value={guardrails.fallback_message || ''} onChange={(event) => patchGuardrails({ fallback_message: event.target.value })} />
                        </Col>
                        <Col xs={24} md={12}>
                          <FieldHeader label="Max response length" confidence={fieldConfidenceMap?.['guardrails.max_response_length']} />
                          <Input value={guardrails.max_response_length || 500} onChange={(event) => patchGuardrails({ max_response_length: Number(event.target.value || 0) })} />
                        </Col>
                      </Row>
                    </Space>
                  </Card>
                </Space>
              </Col>

              <Col xs={24} xl={7}>
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                  <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: 18, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
                    <Space direction="vertical" size={8}>
                      <Title level={5} style={{ margin: 0 }}>Lưu sẽ xảy ra gì?</Title>
                      <Text type="secondary">
                        Nếu company đang bật versioning, hệ thống sẽ tạo version mới và áp dụng ngay. Nếu chưa dùng versioning, cấu hình hiện tại sẽ bị ghi đè.
                      </Text>
                    </Space>
                  </Card>

                  <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: 18, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
                    <Space direction="vertical" size={8}>
                      <Title level={5} style={{ margin: 0 }}>Cần kiểm tra lại</Title>
                      <Text type="secondary">
                        Các field có badge “Suy luận thấp” là những phần được nội suy yếu từ hội thoại. Bạn nên rà lại trước khi áp dụng vào agent thật.
                      </Text>
                    </Space>
                  </Card>
                </Space>
              </Col>
            </Row>
          </Space>
        </div>

        <div style={{ padding: '12px 0px 0px', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 18, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Sẵn sàng lưu</Text>
              <Text type="secondary">Bạn có thể quay lại bước FAQ nếu muốn chỉnh knowledge trước khi áp dụng cấu hình.</Text>
            </div>
            <Space>
              <Button onClick={onBack}>Quay lại bước FAQ</Button>
              <Button onClick={onSaveDraft}>Lưu nháp</Button>
              <Button type="primary" onClick={onApply}>Lưu và áp dụng</Button>
            </Space>
          </div>
        </div>
      </div>
    </Modal>
  );
}
